import { useEffect, useState } from 'react';
import { Icon } from '../components/Icon';
import { api, ApiError } from '../lib/api';
import { KYC_OPTIONS, KYC_PIPELINE, riskTone, RISK_LABEL } from '../lib/crm';
import { initials, money, tone } from '../lib/format';
import { useSession } from '../lib/session';
import '../styles/crm.css';

interface Cp {
  id: string;
  name: string;
  country: string | null;
  kycStatus: string;
  kycLabel: string;
  riskLevel: string;
  outstandingMinor: number;
  currency: string;
}

function StatusModal({ cp, onClose, onSaved }: { cp: Cp; onClose: () => void; onSaved: () => void }) {
  const [kyc, setKyc] = useState(cp.kycStatus);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const save = async () => {
    setBusy(true);
    setErr(null);
    try {
      await api.patch(`/counterparties/${cp.id}`, { kycStatus: kyc });
      onSaved();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'No se pudo actualizar.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="modal-scrim" onMouseDown={onClose}>
      <div className="modal" onMouseDown={(e) => e.stopPropagation()} style={{ maxWidth: 400 }}>
        <div className="modal__head">
          <div>
            <span className="eyebrow">KYC Verification</span>
            <h2 className="h2" style={{ marginTop: 4, fontSize: 18 }}>{cp.name}</h2>
          </div>
          <button className="icon-btn" onClick={onClose}><Icon name="x" size={18} /></button>
        </div>
        <div className="modal__body">
          <div className="field">
            <label>Verification status</label>
            <select className="select" value={kyc} onChange={(e) => setKyc(e.target.value)}>
              {KYC_OPTIONS.map((o) => <option key={o.code} value={o.code}>{o.label}</option>)}
            </select>
          </div>
          {err && <div className="act-error"><Icon name="alert" size={16} />{err}</div>}
        </div>
        <div className="modal__foot">
          <button className="btn btn--ghost" onClick={onClose}>Cancelar</button>
          <button className="btn btn--primary" onClick={save} disabled={busy}>{busy ? 'Guardando…' : 'Actualizar estado'}</button>
        </div>
      </div>
    </div>
  );
}

export function Kyc() {
  const { company } = useSession();
  const [rows, setRows] = useState<Cp[] | null>(null);
  const [editing, setEditing] = useState<Cp | null>(null);

  const load = () => {
    if (!company) return;
    setRows(null);
    api.get<Cp[]>('/counterparties').then(setRows).catch(() => setRows([]));
  };
  useEffect(load, [company]);

  return (
    <main className="main main--wide">
      <div className="page-head">
        <div className="page-head__title">
          <span className="eyebrow">Cumplimiento</span>
          <h1 className="h1">KYC y AML</h1>
          <p className="page-head__sub">The verification status of each counterparty, by stage. Before dealing with someone, they should be approved.</p>
        </div>
      </div>

      {!rows ? (
        <div className="faint" style={{ padding: 40 }}>Loading…</div>
      ) : (
        <div className="kyc-cols">
          {KYC_PIPELINE.map((col) => {
            const items = rows.filter((c) => col.match.includes(c.kycStatus));
            return (
              <div key={col.code}>
                <div className="kyc-col__head">
                  <span className="kyc-col__title">{col.label}</span>
                  <span className="tag">{items.length}</span>
                </div>
                {items.length === 0 ? (
                  <div className="empty" style={{ padding: 18, fontSize: 12.5 }}>Empty</div>
                ) : (
                  items.map((c) => (
                    <div key={c.id} className="kyc-card" onClick={() => setEditing(c)}>
                      <div className="row gap-10 center">
                        <span className="cp-avatar" style={{ background: tone(c.id), width: 28, height: 28, fontSize: 11 }}>{initials(c.name)}</span>
                        <div style={{ minWidth: 0 }}>
                          <div className="kyc-card__name">{c.name}</div>
                          <div className="kyc-card__meta">{c.country ?? '—'}</div>
                        </div>
                      </div>
                      <div className="kyc-card__foot">
                        <span className={`pill pill--${riskTone(c.riskLevel)}`}><span className="pill__dot" />{RISK_LABEL[c.riskLevel] ?? c.riskLevel}</span>
                        {c.outstandingMinor > 0 && <span className="num" style={{ fontSize: 12, color: 'var(--graphite)' }}>{money(c.outstandingMinor, c.currency)}</span>}
                      </div>
                    </div>
                  ))
                )}
              </div>
            );
          })}
        </div>
      )}

      {editing && <StatusModal cp={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </main>
  );
}
